import { memo } from "react";
import { Button } from "ui/components/Button/Button";
import { MarketplaceDetails } from "services/api/sales/listings/types";
import { ListingContact } from "services/api/settings/listings/types";
import { DealerCardProps } from "./types";
import { Section } from "../primitives";
import Avatar from "./Avatar";
import LocationMap from "./LocationMap";

/** Za tel: linkove broj mora biti bez razmaka i znakova. */
const toDialNumber = (phone: string) => phone.replace(/[^\d+]/g, "");

/** Za pozive ima prednost mobitel; fiksni je zamjena kad mobitela nema. */
const primaryPhoneOf = (contact: ListingContact) =>
  contact.mobilePhoneNumber || contact.phoneNumber || "";

/** Lokacija oglasa u jednom retku — od najuže odrednice prema najširoj. */
const manualAddressOf = (details?: MarketplaceDetails | null) =>
  [
    details?.locationManualAddress,
    details?.locationSettlementCode,
    details?.locationMunicipalityCode,
    details?.locationCountyCode,
  ]
    .filter((part) => !!part?.trim())
    .join(", ");

const mapDisplayLabel = (display?: string | null) => {
  if (display === "approximate") return "Približna lokacija na karti";
  if (display === "exact") return "Točna lokacija na karti";
  return null;
};

// Kontakt prodavatelja — firma na vrhu (nosi povjerenje kupca), pa djelatnici s oglasa
// i lokacija s kartom.
const DealerCard: React.FC<DealerCardProps> = ({
  contacts = [],
  offices = [],
  company,
  details,
}) => {
  // Bez poslovnice vrijedi ručno upisana lokacija oglasa.
  const manualAddress = offices.length === 0 ? manualAddressOf(details) : "";
  const displayLabel = mapDisplayLabel(details?.locationDisplay);

  if (contacts.length === 0 && offices.length === 0 && !manualAddress) return null;

  return (
    <Section title="Kontakt prodavatelja">
      {company?.name && (
        <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-200 -mx-4 px-4">
          {/* Logo samo ako ga tenant ima — bez zamjenske ikone ili inicijala. */}
          {company.logoUrl && (
            <img
              src={company.logoUrl}
              alt={company.name}
              className="h-10 max-w-[120px] object-contain flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-800 truncate">
              {company.name}
            </div>
            <div className="text-xs text-slate-500">Prodavatelj vozila</div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5 mb-3">
        {contacts.map((contact) => {
          const phone = primaryPhoneOf(contact);
          const dial = phone ? toDialNumber(phone) : "";

          return (
            <div key={contact.userId}>
              <div className="flex items-center gap-3 mb-3">
                <Avatar name={contact.fullName} />
                <div className="min-w-0 text-sm">
                  <div className="text-md font-semibold text-slate-800 truncate">
                    {contact.fullName}
                  </div>
                  {phone && (
                    <a href={`tel:${dial}`} className="block text-slate-700 hover:text-sky-600">
                      {phone}
                    </a>
                  )}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="block text-slate-500 truncate hover:text-sky-600"
                    >
                      {contact.email}
                    </a>
                  )}
                </div>
              </div>

              {/* Akcije uvijek stoje; „Nazovi" je bez broja onemogućen, da kartica ne
                  mijenja oblik ovisno o tome je li djelatniku upisan telefon. */}
              <div className="grid grid-cols-2 gap-2">
                {/* Bez akcije dok se ne napravi sustav za slanje poruka. */}
                <Button
                  variant="outlined"
                  size="full"
                  startIcon="/media/icons/message.svg"
                  className="items-center justify-center"
                >
                  Pošalji poruku
                </Button>

                {phone ? (
                  <a href={`tel:${dial}`} className="block">
                    <Button
                      variant="primary"
                      size="full"
                      startIcon="/media/icons/phone.svg"
                      className="items-center justify-center"
                    >
                      Nazovi
                    </Button>
                  </a>
                ) : (
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/phone.svg"
                    className="items-center justify-center opacity-50 cursor-not-allowed"
                    disabled
                  >
                    Nazovi
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* `-mx-4 px-4` poništava padding sekcije samo za crtu — ona ide od ruba do ruba
          kartice, dok sadržaj ostaje poravnat s ostatkom. */}
      {(offices.length > 0 || manualAddress) && (
        <div className="border-t border-slate-200 pt-3 mb-3 -mx-4 px-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Vozilo možete pogledati
          </div>

          <div className="flex flex-col gap-4 text-sm">
            {offices.map((office) => (
              <div key={office.officeId}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800">{office.name}</div>
                  {office.address && <div className="text-slate-500">{office.address}</div>}
                </div>

                <LocationMap
                  address={[office.address, office.city].filter(Boolean).join(", ")}
                  latitude={office.latitude}
                  longitude={office.longitude}
                />
              </div>
            ))}

            {manualAddress && (
              <div>
                <div className="text-slate-700">{manualAddress}</div>
                <LocationMap address={manualAddress} />
              </div>
            )}
          </div>

          {displayLabel && (
            <div className="text-xs text-slate-400 mt-2">{displayLabel}</div>
          )}
        </div>
      )}
    </Section>
  );
};

export default memo(DealerCard);
